import { faPlus } from "@fortawesome/pro-regular-svg-icons";
import { useTranslation } from "react-i18next";
import { useDroppable } from "@dnd-kit/core";

import { Icon } from "@/components";
import { classNames } from "@/utils/classNames";

import { PriorityDefinitionItem, PriorityDefinitionItemCard } from "./PriorityDefinitionItemCard";
import { priorityItemKey } from "./movePriorityItem";

export interface PriorityDefinitionSectionProps<
	TItem extends PriorityDefinitionItem,
	TLevel extends string,
> {
	priorityLevel: TLevel;
	items: TItem[];
	onAddItem: (priorityLevel: TLevel) => void;
	onEditItem: (item: TItem) => void;
	onDeleteItem: (item: TItem) => void;
	/**
	 * Enables dragging definitions in and out of this box (PREC-3908). Off by default so a caller
	 * that has not wrapped the boxes in a `PriorityDefinitionBoard` — and therefore has no
	 * `DndContext` — keeps working unchanged.
	 */
	movable?: boolean;
}

const getPriorityBadgeLabel = (level: string): string => {
	switch (level) {
		case "1":
			return "P1";
		case "2":
			return "P2";
		case "3":
			return "P3";
		case "disqualified":
			return "DQ";
		default:
			return "?";
	}
};

const getPriorityBadgeClassName = (level: string): string => {
	switch (level) {
		case "1":
			return "text-text-success border border-text-success bg-background-success";
		case "2":
			return "text-text-warning border border-text-warning bg-background-warning";
		case "3":
			return "border border-error text-error bg-error-light";
		case "disqualified":
			return "text-white bg-error";
		default:
			return "text-text-meta bg-border-light";
	}
};

const getPriorityLabel = (level: string, t: (key: string) => string): string => {
	switch (level) {
		case "1":
			return t("list.signals.priority1");
		case "2":
			return t("list.signals.priority2");
		case "3":
			return t("list.signals.priority3");
		case "disqualified":
			return t("list.signals.disqualified");
		default:
			return t("list.signals.unknown");
	}
};

function PriorityBadge({ priorityLevel }: { priorityLevel: string }) {
	return (
		<span
			className={classNames(
				"cursor-default flex w-[24px] h-[24px] text-xxs font-bold rounded-full justify-center items-center shrink-0",
				getPriorityBadgeClassName(priorityLevel)
			)}
		>
			{getPriorityBadgeLabel(priorityLevel)}
		</span>
	);
}

export function PriorityDefinitionSection<
	TItem extends PriorityDefinitionItem,
	TLevel extends string,
>({
	priorityLevel,
	items,
	onAddItem,
	onEditItem,
	onDeleteItem,
	movable = false,
}: PriorityDefinitionSectionProps<TItem, TLevel>) {
	const { t } = useTranslation();
	const { setNodeRef, isOver } = useDroppable({ id: priorityLevel, disabled: !movable });

	return (
		<div
			ref={movable ? setNodeRef : undefined}
			className={classNames(
				"mb-6 rounded-lg transition-colors",
				// The whole box is the drop target, so it has to say so while a card is over it —
				// including an empty box, whose placeholder is the only thing to aim at.
				isOver ? "outline-2 outline-dashed outline-brand-light outline-offset-4" : ""
			)}
		>
			<div className="flex items-center justify-between mb-3 gap-3">
				<div className="flex items-center gap-2">
					<PriorityBadge priorityLevel={priorityLevel} />
					<h4 className="text-sm font-semibold text-text">{getPriorityLabel(priorityLevel, t)}</h4>
					<span className="text-sm text-text-meta">
						({items.length} {t("list.signals.items")})
					</span>
				</div>
				<button
					className="flex items-center gap-1 px-2 py-1 text-sm font-medium text-brand-light hover:bg-light-blue rounded transition-colors"
					type="button"
					onClick={() => onAddItem(priorityLevel)}
				>
					<Icon className="h-3 w-3" icon={faPlus} />
					{t("list.signals.addPriorityItem")}
				</button>
			</div>
			{items.length > 0 ? (
				<div className="space-y-2">
					{items.map((item, index) => (
						<PriorityDefinitionItemCard
							key={item.id || `${priorityLevel}-${index}`}
							dragId={movable ? priorityItemKey({ title: item.title, priorityLevel }) : undefined}
							item={item}
							onDelete={onDeleteItem}
							onEdit={onEditItem}
						/>
					))}
				</div>
			) : (
				<div className="p-4 rounded-lg border border-dashed border-border-light bg-brand-white text-center">
					<p className="text-sm text-text-meta">{t("list.signals.noPriorityItemsDefined")}</p>
				</div>
			)}
		</div>
	);
}
